import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Tag, Ban, X, Loader2, AlertTriangle, Package, Beaker, Eye,
  Calendar, ArrowDownToLine, ArrowUpFromLine, Clock,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatDate, formatDateTime, formatMoney } from '@/lib/api'

interface Lote {
  id: number
  codigo: string
  op_id: number
  op_numero: number
  produto_id: number
  produto_codigo: string
  produto_descricao: string
  data_producao: string | null
  data_validade: string | null
  dias_para_vencer: number | null
  status: string
  quantidade_produzida: number
  observacao: string | null
}

const STATUS_COR: Record<string, string> = {
  aberto: 'bg-amber-100 text-amber-800',
  fechado: 'bg-emerald-100 text-emerald-800',
  bloqueado: 'bg-rose-100 text-rose-700',
  consumido: 'bg-ink-200 text-ink-700',
}

export default function LotesPage() {
  const qc = useQueryClient()
  const [status, setStatus] = useState<string>('')
  const [detalhe, setDetalhe] = useState<Lote | null>(null)

  const { data: lotes = [], isLoading } = useQuery<Lote[]>({
    queryKey: ['lotes', status],
    queryFn: async () => (await api.get('/industrial/lotes', { params: { status: status || undefined } })).data,
  })

  const bloquear = useMutation({
    mutationFn: async ({ id, motivo }: { id: number; motivo: string }) =>
      (await api.post(`/industrial/lotes/${id}/bloquear`, null, { params: { motivo } })).data,
    onSuccess: () => {
      toast.success('Lote bloqueado')
      qc.invalidateQueries({ queryKey: ['lotes'] })
    },
  })

  const vencendo = lotes.filter(l => l.dias_para_vencer !== null && l.dias_para_vencer <= 30 && l.status !== 'bloqueado')

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Tag className="text-brand-600" /> Lotes de Produção
          </h1>
          <p className="text-ink-600 text-sm">
            Rastreabilidade bidirecional: clique em "Rastreio" pra ver quais MPs viraram este lote e pra onde ele foi.
          </p>
        </div>
      </div>

      {vencendo.length > 0 && (
        <div className="card card-body bg-amber-50 border-amber-200 flex items-center gap-3">
          <AlertTriangle className="text-amber-600 shrink-0" />
          <div className="flex-1 text-sm text-amber-900">
            <strong>{vencendo.length}</strong> lote(s) vencendo em até 30 dias.
          </div>
          <button onClick={() => setStatus('fechado')} className="btn-secondary !py-1 text-xs">Ver fechados</button>
        </div>
      )}

      <div className="card card-body">
        <div className="flex gap-2 flex-wrap text-sm items-center">
          <span className="text-ink-600">Status:</span>
          {['', 'aberto', 'fechado', 'bloqueado', 'consumido'].map(s => (
            <button key={s || 'todos'} onClick={() => setStatus(s)}
              className={`px-3 py-1 rounded text-xs ${status === s ? 'bg-brand-600 text-white' : 'bg-ink-100 text-ink-700'}`}>
              {s === '' ? 'Todos' : s}
            </button>
          ))}
        </div>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr><th>Código</th><th>Produto</th><th>OP</th>
              <th>Produção</th><th>Validade</th>
              <th className="text-right">Qtd</th><th>Status</th><th className="w-32"></th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={8} className="text-center py-8">Carregando...</td></tr>}
            {!isLoading && lotes.length === 0 && (
              <tr><td colSpan={8} className="text-center py-8 text-ink-500">Nenhum lote. Lotes são criados automaticamente ao iniciar uma OP.</td></tr>
            )}
            {lotes.map(l => (
              <tr key={l.id}
                onClick={() => setDetalhe(l)}
                className="hover:bg-violet-50 cursor-pointer">
                <td className="font-mono text-xs">{l.codigo}</td>
                <td>
                  <div className="font-mono text-xs">{l.produto_codigo}</div>
                  <div className="text-xs text-ink-600">{l.produto_descricao}</div>
                </td>
                <td onClick={e => e.stopPropagation()}>
                  <Link to={`/industrial/ordens/${l.op_id}`} className="text-brand-600 hover:underline">
                    #{l.op_numero}
                  </Link>
                </td>
                <td className="text-xs">{formatDate(l.data_producao)}</td>
                <td className="text-xs">
                  {l.data_validade ? (
                    <span className={l.dias_para_vencer !== null && l.dias_para_vencer <= 30 ? 'text-amber-700 font-semibold' : ''}>
                      {formatDate(l.data_validade)}
                      {l.dias_para_vencer !== null && <> ({l.dias_para_vencer >= 0 ? `${l.dias_para_vencer}d` : 'vencido'})</>}
                    </span>
                  ) : '—'}
                </td>
                <td className="text-right font-semibold">{l.quantidade_produzida.toLocaleString('pt-BR')}</td>
                <td><span className={`text-xs px-2 py-0.5 rounded ${STATUS_COR[l.status]}`}>{l.status}</span></td>
                <td onClick={e => e.stopPropagation()}>
                  <div className="flex gap-1">
                    <button onClick={() => setDetalhe(l)} className="btn-secondary !py-1 !px-2 text-xs">
                      <Eye size={12} /> Rastreio
                    </button>
                    {l.status !== 'bloqueado' && (
                      <button onClick={() => {
                        const m = prompt('Motivo do bloqueio:')
                        if (m) bloquear.mutate({ id: l.id, motivo: m })
                      }} className="btn-secondary !py-1 !px-2 text-xs text-rose-700">
                        <Ban size={12} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detalhe && <RastreioModal lote={detalhe} onClose={() => setDetalhe(null)} />}
    </div>
  )
}

function RastreioModal({ lote, onClose }: { lote: Lote; onClose: () => void }) {
  const { data } = useQuery<any>({
    queryKey: ['lote-rastreio', lote.id],
    queryFn: async () => (await api.get(`/industrial/lotes/${lote.id}/rastreio`)).data,
  })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h3 className="font-semibold">Rastreio — Lote {lote.codigo}</h3>
            <div className="text-xs text-ink-600">{lote.produto_descricao}</div>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        {!data && <div className="p-8 text-center">Carregando rastreio...</div>}
        {data && (
          <div className="p-5 space-y-5">
            <div className="grid md:grid-cols-4 gap-3 text-sm">
              <div><span className="text-ink-500 text-xs">Produção:</span><br/><strong>{formatDate(data.lote.data_producao)}</strong></div>
              <div><span className="text-ink-500 text-xs">Validade:</span><br/><strong>{formatDate(data.lote.data_validade) || '—'}</strong></div>
              <div><span className="text-ink-500 text-xs">Qtd produzida:</span><br/><strong>{data.lote.quantidade_produzida}</strong></div>
              <div><span className="text-ink-500 text-xs">OP origem:</span><br/><Link to={`/industrial/ordens/${data.lote.op_id}`} className="text-brand-600 hover:underline font-semibold">#{data.lote.op_numero}</Link></div>
            </div>

            {/* MPs consumidas */}
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <Beaker size={14} className="text-amber-600" />
                ← Matérias-primas consumidas ({data.mps_consumidas.length})
              </h4>
              {data.mps_consumidas.length === 0 ? (
                <div className="text-xs text-ink-500">Nenhuma MP rastreada — produção sem composição definida.</div>
              ) : (
                <table className="table text-xs">
                  <thead><tr><th>Código</th><th>MP</th><th className="text-right">Qtd</th><th className="text-right">Custo un.</th><th className="text-right">Custo total</th></tr></thead>
                  <tbody>
                    {data.mps_consumidas.map((m: any, i: number) => (
                      <tr key={i}>
                        <td className="font-mono">{m.mp_codigo}</td>
                        <td>{m.mp_nome}</td>
                        <td className="text-right">{m.quantidade} {m.unidade}</td>
                        <td className="text-right">{formatMoney(m.custo_unitario)}</td>
                        <td className="text-right">{formatMoney(m.custo_total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Produtos gerados */}
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <ArrowDownToLine size={14} className="text-emerald-600" />
                → Produtos gerados que entraram no estoque ({data.produtos_gerados.length})
              </h4>
              {data.produtos_gerados.length === 0 ? (
                <div className="text-xs text-ink-500">Lote ainda aberto — produção não concluída.</div>
              ) : (
                <table className="table text-xs">
                  <thead><tr><th>Produto</th><th className="text-right">Qtd</th><th className="text-right">Saldo pós</th><th>Depósito</th><th>Quando</th></tr></thead>
                  <tbody>
                    {data.produtos_gerados.map((p: any) => (
                      <tr key={p.movimento_id}>
                        <td><div className="font-mono">{p.produto_codigo}</div><div>{p.produto_descricao}</div></td>
                        <td className="text-right font-semibold">{p.quantidade}</td>
                        <td className="text-right">{p.saldo_pos}</td>
                        <td>#{p.deposito_id}</td>
                        <td>{formatDateTime(p.criado_em)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Saídas vinculadas */}
            <div>
              <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                <ArrowUpFromLine size={14} className="text-rose-600" />
                → Saídas vinculadas (vendas, transferências) ({data.saidas_vinculadas.length})
              </h4>
              {data.saidas_vinculadas.length === 0 ? (
                <div className="text-xs text-ink-500">Nenhuma saída ainda — produto do lote ainda está em estoque.</div>
              ) : (
                <table className="table text-xs">
                  <thead><tr><th>Produto</th><th className="text-right">Qtd</th><th>Origem</th><th>Quando</th></tr></thead>
                  <tbody>
                    {data.saidas_vinculadas.map((s: any) => (
                      <tr key={s.movimento_id}>
                        <td><div className="font-mono">{s.produto_codigo}</div></td>
                        <td className="text-right">{s.quantidade}</td>
                        <td className="text-xs">{s.origem} #{s.origem_id}</td>
                        <td>{formatDateTime(s.criado_em)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
